import { Tracing } from "@tento-core/tracing";
import { CalendarMonth } from "@tento-ui/components/calendar-month/calendar-month";
import { CalendarMonthGesture } from "@tento-ui/components/calendar-month/calendar-month-gesture";
import { TimezoneConfirmationModal } from "@tento-ui/components/calendar-month/timezone-confirmation-modal";
import { type RecurringEditAction, RecurringEditModal } from "@tento-ui/components/calendar/delete-recurring-event-modal";
import type { ListItem } from "@tento-ui/components/entry-list/list-item";
import { Actor, EventPatch } from "@tento-lona/sheets";
import { DateTime, DateTimeRegion, NaiveDate, TimezoneRegion, Tzname } from "@tento-chrono";
import { AllDaySpan } from "@tento-lona/sheets-ui/plugins/layout/grid/month/all-day-span.ts";
import { type AllDayRange, EventItemPresentation } from "@tento-lona";
import type { OccurrenceMove } from "./events-source.ts";
import { EventId, EventItem, EventItemTimed } from "@tento-lona/sheets";
import { EventWriteRefusal } from "../column-calendar/components/event-write-refusal.ts";
import type { MonthActionsContext } from "./month-actions-context.ts";

/**
 * The month view's write gestures: an all-day span moved or resized, an
 * entry row dragged to another day, and the recurring-edit scope prompt
 * they share. Each gate reads `EventAccess` (`ctx.accessOf`), each write
 * goes through the month's `EventsSource`, and a refusal is reported with
 * its reason.
 */
export class MonthWriteGestures {
  readonly #ctx: MonthActionsContext;

  constructor(ctx: MonthActionsContext) {
    this.#ctx = ctx;
  }

  // ── Gesture handlers (moved from CalendarMonthHost in Phase F.5c) ────
  //
  // These hook the calendar-month gesture manager directly. The layout
  // (`CalendarMonthHost`) constructs the manager and registers handlers
  // pointing here so the layout file holds no event-mutation logic.

  async onSpanMoved(e: {
    span: CalendarMonth.Span;
    segment: CalendarMonth.SpanSegmentInfo;
    fromDate: NaiveDate;
    toDate: NaiveDate;
    info: CalendarMonthGesture.Info;
  }): Promise<void> {
    this.#ctx.$calendar.highlightCell(null);
    const event = AllDaySpan.eventOf(e.span);
    if (!event) return;

    // An event the user may not move (a read-only list, calendar or event): the dragged span snaps back.
    if (!this.#ctx.accessOf(event).move) return;
    const dayOffset = e.toDate.differenceInDays(e.fromDate);

    // Google Calendar uses exclusive end date (day after last day).
    const newRange: AllDayRange = {
      startDate: event.time.start.addDays(dayOffset),
      endDate: event.time.end.addDays(dayOffset).add({ days: 1 }),
    };

    if (event.inner.recurrence?.length) {
      this.#confirmRecurringEdit({ kind: "all-day", event, range: newRange }, "move");
      return;
    }
    // A refusal (its move already taken back) is reported with its reason.
    const moved = await EventWriteRefusal.attempt(event, "move the event", this.#reportRefusal, () =>
      this.#ctx.eventsSource.updateDurable(event, EventPatch.of(newRange), Actor.EXTERNAL).settled);
    if (!moved) return;
    this.#ctx.delegate.rebindCalendar();
  }

  async onSpanResized(e: {
    span: CalendarMonth.Span;
    originalRange: NaiveDate.Range;
    newRange: NaiveDate.Range;
    info: CalendarMonthGesture.Info;
  }): Promise<void> {
    this.#ctx.$calendar.highlightCell(null);
    const event = AllDaySpan.eventOf(e.span);
    if (!event) return;

    // An event the user may not move (a read-only list, calendar or event): the resized span snaps back.
    if (!this.#ctx.accessOf(event).move) return;

    // Span range carries inclusive end day; Google Calendar event payloads
    // use exclusive end (day after last day), matching `onSpanMoved`.
    const newRange: AllDayRange = {
      startDate: e.newRange.start,
      endDate: e.newRange.end.add({ days: 1 }),
    };

    if (event.inner.recurrence?.length) {
      this.#confirmRecurringEdit({ kind: "all-day", event, range: newRange }, "resize");
      return;
    }
    const resized = await EventWriteRefusal.attempt(event, "resize the event", this.#reportRefusal, () =>
      this.#ctx.eventsSource.updateDurable(event, EventPatch.of(newRange), Actor.EXTERNAL).settled);
    if (!resized) return;
    this.#ctx.delegate.rebindCalendar();
  }

  /**
   * Show the recurring-edit picker for a move/resize of a recurring
   * occurrence, then dispatch through `eventsSource.moveOccurrence` on
   * the user's chosen scope. Cancel → no-op. Non-blocking — the
   * gesture handler returns immediately and the modal callback
   * commits the change asynchronously.
   */
  #confirmRecurringEdit(
    move: OccurrenceMove,
    action: RecurringEditAction,
  ): void {
    const title = move.event.title() ?? "Untitled Event";
    RecurringEditModal.show(action, title, async (scope) => {
      // The modal invokes this callback fire-and-forget; surface failures
      // (e.g. master-not-found throw, network error) instead of letting
      // them become an unhandled rejection.
      const report: EventWriteRefusal.Report = (event, refused, error) => {
        Tracing.warn("ui")("recurring edit failed", { action, error });
        this.#reportRefusal(event, refused, error);
      };
      const edited = await EventWriteRefusal.attempt(move.event, `${action} the event`, report, () =>
        this.#ctx.eventsSource.moveOccurrence(move, scope, Actor.EXTERNAL));
      if (edited) this.#ctx.delegate.rebindCalendar();
    });
  }


  async onEntryRowMoved(e: {
    eventData: ListItem;
    fromCell: CalendarMonthGesture.Info;
    toCell: CalendarMonthGesture.Info;
  }): Promise<void> {
    this.#ctx.$calendar.highlightCell(null);
    const eventId = EventId.parse(e.eventData.id ?? "");
    if (!eventId) return;

    const dayOffset = e.toCell.date.differenceInDays(e.fromCell.date);
    if (dayOffset === 0) {
      return;
    }

    const raw = this.#ctx.eventsSource.peek(eventId);
    const event = raw
      ? EventItem.of(raw.inner, raw.origin)
      : null;
    if (!event || !(event instanceof EventItemTimed)) return;
    // An event the user may not move (a read-only list, calendar or event): the dragged row snaps back.
    if (!this.#ctx.accessOf(event).move) return;

    // Build timezone options for the modal
    const options = await this.#buildTimezoneOptions(event, dayOffset);

    // Show modal if multiple options, otherwise use default
    let selectedTz: Tzname;
    if (options.length <= 1) {
      const only = options[0];
      selectedTz = (only != null ? Tzname.parseOpt(only.value) : null) ??
        this.#ctx.timezonePreferences.effectiveTimezoneName();
    } else {
      const category = EventItemPresentation.categoryItem(event, this.#ctx.sdkEvents);
      const result = await TimezoneConfirmationModal.show({
        title: "Select Timezone",
        description: "Please specify a timezone for the new event time.",
        eventTitle: event.title() ?? "Untitled Event",
        eventIcon: category?.emoji ?? "📅",
        eventIconBg: category == null ? "var(--gray-dimmed)" : category.colorOpt() ?? "var(--gray)",
        options,
      });

      if (!result) return; // Cancelled
      const picked = Tzname.parseOpt(result.selectedTimezone);
      if (picked == null) return; // Not a zone the runtime knows
      selectedTz = picked;
    }

    // Add `dayOffset` days at the UTC level. This preserves the
    // event's wall-clock time on the new date in any non-DST timezone
    // (with at most ±1h drift across DST boundaries — acceptable, and
    // the same trade-off the full-day path makes via NaiveDate.addDays).
    //
    // The previous implementation round-tripped UTC → wall-clock in
    // `selectedTz` → `.ndt` (naive) → `toWallClock` → UTC and applied
    // the tz offset twice on the return leg, shifting the time by ~5h
    // on every move. `selectedTz` is computed above for the
    // confirmation modal's UI label only; it does not enter the math.
    void selectedTz;
    const newStart = event.time.time.start.add({ days: dayOffset });
    const newEnd = event.time.time.end.add({ days: dayOffset });

    if (event.inner.recurrence?.length) {
      this.#confirmRecurringEdit({ kind: "timed", event, range: { start: newStart, end: newEnd } }, "move");
      return;
    }
    const moved = await EventWriteRefusal.attempt(event, "move the event", this.#reportRefusal, () =>
      this.#ctx.eventsSource.updateDurable(event, EventPatch.of({
        time: { start: newStart, end: newEnd },
      }), Actor.EXTERNAL).settled);
    if (!moved) return;

    this.#ctx.delegate.rebindCalendar();
  }


  async #buildTimezoneOptions(
    event: EventItemTimed,
    dayOffset: number,
  ): Promise<TimezoneConfirmationModal.TimezoneOption[]> {
    const options: TimezoneConfirmationModal.TimezoneOption[] = [];
    const eventStart = event.time.time.start;

    // Get effective timezone for local time display
    const effectiveTz = this.#ctx.timezonePreferences.effectiveTimezoneName();
    const effectiveTzRegion = await TimezoneRegion.get(effectiveTz);
    const eventEnd = event.time.time.end;

    // Helper to build option with description and resolved time range
    const buildOption = async (
      label: string,
      tzName: Tzname,
      isDefault?: boolean,
    ): Promise<TimezoneConfirmationModal.TimezoneOption> => {
      const tz = await TimezoneRegion.get(tzName);

      // Calculate new start time in given timezone
      const startWall = tz.toTz(eventStart);
      const newStartWall = startWall.add({ days: dayOffset });
      const newStartUtc = tz.toWallClock(newStartWall.ndt).asUtc();
      const newStartLocal = effectiveTzRegion.toTz(newStartUtc);

      // Calculate new end time in given timezone
      const endWall = tz.toTz(eventEnd);
      const newEndWall = endWall.add({ days: dayOffset });
      const newEndUtc = tz.toWallClock(newEndWall.ndt).asUtc();
      const newEndLocal = effectiveTzRegion.toTz(newEndUtc);

      const formatTime = (ndt: {
        time: {
          format: (opts: {
            includeSeconds: boolean;
            includeAmPm: boolean;
          }) => string;
        };
      }) => ndt.time.format({ includeSeconds: false, includeAmPm: true });

      return {
        label,
        description: TimezoneConfirmationModal.createTimeRangeDescription(
          {
            startTime: formatTime(newStartWall.ndt),
            endTime: formatTime(newEndWall.ndt),
            tzName: tzName,
          },
          {
            startTime: formatTime(newStartUtc),
            endTime: formatTime(newEndUtc),
            tzName: "UTC",
          },
          {
            startTime: formatTime(newStartLocal.ndt),
            endTime: formatTime(newEndLocal.ndt),
            tzName: effectiveTz,
          },
        ),
        value: tzName,
        ...(isDefault !== undefined ? { isDefault } : {}),
        resolvedTime: DateTimeRegion.Range.fromSingleTz(
          new DateTime.Range(newStartUtc, newEndUtc),
          effectiveTzRegion,
        ),
      };
    };

    // Event's timezone (if available)
    const eventTz = event.inner.startTimeZone == null ? null : Tzname.parseOpt(event.inner.startTimeZone);
    if (eventTz) {
      options.push(await buildOption("Event's original timezone", eventTz));
    }

    // User's effective timezone (already fetched above)
    options.push(
      await buildOption("Your calendar timezone", effectiveTz, true),
    );

    // Browser's timezone (if different)
    const browserTz = this.#ctx.timezonePreferences.runtimeTimezoneName();
    if (browserTz !== effectiveTz) {
      options.push(await buildOption("Your current location", browserTz));
    }

    return options;
  }


  /** How a refused month write is told to the user (the host's recovery, else the default toast). */
  get #reportRefusal(): EventWriteRefusal.Report {
    return this.#ctx.ui.reportWriteRefused ?? EventWriteRefusal.toast;
  }

}
